import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Helper } from './helper';
@Injectable({
  providedIn: 'root'
})
export class ApiRequest {
  apiURL: string = environment.apiURL;
  private refreshTokenPromise: Promise<string> | null = null;
  private http = inject(HttpClient);
  private helperService = inject(Helper);
  
  constructor() { 
    this.apiURL += 'api/';
  }

  private _extradefaultParam(fullEndpoint:any) {
    const authType = this.helperService.getAuthType();

    if (fullEndpoint.indexOf('?') != -1) {
      return fullEndpoint + '&auth_type=' + encodeURIComponent(authType);// environment.authType;
    } else {
      return fullEndpoint + '?auth_type=' + encodeURIComponent(authType);// environment.authType;
    }
  }

  private shouldAttachAuthHeader(urlSlug: string): boolean {
    const publicEndpointPatterns = [
      /^auth\/(?:login|register|verify_otp|check_login)(?:\?|$)/,
      /^listing(?:\?|$)/,
      /^listing\/offers(?:\/\d+)?(?:\?|$)/,
      /^listing\/amenities(?:\/\d+)?(?:\?|$)/,
      /^pages(?:\/|$|\?)/,
      /^settings(?:\?|$)/,
      /^enquiry(?:\?|$)/,
    ];

    const normalizedSlug = `${urlSlug}`.replace(/^\/+/, '');

    return !publicEndpointPatterns.some(pattern => pattern.test(normalizedSlug));
  }

  private async buildAuthHeaders(urlSlug: string, options:any = {}) {
    if (!this.shouldAttachAuthHeader(urlSlug)) {
      return {};
    }

    let userToken = this.helperService.checkGenerateJwtToken();
    if (userToken && this.helperService.isJwtTokenNearExpiry(userToken)) {
      this.helperService.dbg('JWT close to expiry, requesting refresh from ApiRequest');
      userToken = await this.refreshJwtToken();
    }

    if (userToken) {
      options["Authorization"] = 'Bearer ' + userToken;

      return this._setHeader(options);
    }

    return {};
  }

  private refreshJwtToken(): Promise<string> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    const userToken = this.helperService.getUserToken();
    const authType = this.helperService.getAuthType();
    if (!userToken) {
      return Promise.resolve('');
    }

    const endpoint = `${this.apiURL}auth/refresh_token?auth_type=${encodeURIComponent(authType)}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${userToken}`
    });

    this.refreshTokenPromise = firstValueFrom(
      this.http.post<any>(endpoint, null, { headers })
    )
      .then(result => {
        const refreshedToken = result?.access_token || result?.data?.access_token || '';

        if (refreshedToken) {
          this.helperService.setUserToken(refreshedToken);
          this.helperService.dbg('JWT refreshed from server', true);
          return refreshedToken;
        }

        this.helperService.dbg('JWT refresh failed', result);
        return userToken;
      })
      .catch(error => {
        this.helperService.dbg('JWT refresh error', error);
        return userToken;
      })
      .finally(() => {
        this.refreshTokenPromise = null;
      });

    return this.refreshTokenPromise;
  }
 

  private _setHeader(options: any = {}) {
    let headers:any = {};
    if (options) {
      const httpHeaders = new HttpHeaders(options);
      headers['headers'] = httpHeaders;
    }

    return headers;
  }

  /* Await api call*/
  sendRequestGet(urlSlug:any): Observable<any> {
    let endpoint = this.apiURL + urlSlug;
    if (this.shouldAttachAuthHeader(urlSlug)) {
      endpoint = this._extradefaultParam(endpoint);
    }

    return from(this.buildAuthHeaders(urlSlug)).pipe(
      switchMap(headerOpt => this.http.get<any>(endpoint, headerOpt))
    );
  }

  sendRequestPost(urlSlug:any, formData:any): Observable<any> {
    this.helperService.dbg('sendRequestPost urlSlug',urlSlug);
    this.helperService.dbg('sendRequestPost formData',formData);

    let endpoint = this.apiURL + urlSlug;
    if (this.shouldAttachAuthHeader(urlSlug)) {
      endpoint = this._extradefaultParam(endpoint);
    }

    let apiData: FormData;
    if (formData instanceof FormData) {
      apiData = formData;
    } else {
      apiData = new FormData();
      for (var key in formData) {
        apiData.append(key, formData[key]);
      }
    }

    return from(this.buildAuthHeaders(urlSlug)).pipe(
      switchMap(headerOpt => this.http.post<any>(endpoint, apiData, headerOpt))
    );
  }

  sendRequestDelete(urlSlug:any): Observable<any> {
    let endpoint = this.apiURL + urlSlug;

    if (this.shouldAttachAuthHeader(urlSlug)) {
      endpoint = this._extradefaultParam(endpoint);
    }

    return from(this.buildAuthHeaders(urlSlug)).pipe(
      switchMap(headerOpt => this.http.delete(
        endpoint, headerOpt
      ))
    );
  }

  getLodgeDetails(): Observable<any> {
    return this.sendRequestGet('listing');
  }

  getLodgeOffers(): Observable<any> {
    return this.sendRequestGet('listing/offers');
  }

  getSettings(): Observable<any> {
    return this.sendRequestGet('settings');
  }
}
